/**
 * As normas de citação e de lista de referências.
 *
 * O gerador nasceu na **ABNT** (NBR 6023 para a lista, NBR 10520 para a citação no
 * texto) e é ela que vale quando o documento não escolhe nada. A **APA** (7ª edição)
 * entra aqui porque é a norma de boa parte da pós-graduação em educação, psicologia e
 * saúde — e porque, entre as normas que se usam no Brasil, é a que muda a **forma da
 * linha e da citação** sem mudar a mecânica: as duas são autor-data, a lista das duas
 * sai em ordem alfabética, e a citação das duas se escreve no texto.
 *
 * E entram também as **numeradas** — IEEE e Vancouver —, que são o outro bicho: nelas a
 * citação no texto é o **número** (`[1]`, `(1)`) e a lista sai na **ordem da primeira
 * citação**, não em ordem alfabética.
 *
 * O número mora na referência (`numeroDaLista`), e não é contado na hora de desenhar. A
 * razão é a mesma das duas normas: quem é citado primeiro recebe o 1. O número é dado no
 * gesto de **citar** — a primeira citação daquela obra é o que a numera — e fica gravado,
 * para a lista não mudar de ordem conforme o texto for reescrito. Referência ainda sem
 * número recebe um **provisório** na lista (o próximo livre, em ordem alfabética entre as
 * que faltam), e o motor avisa: número provisório é número que ainda não foi citado.
 *
 * O que **não** muda com a norma (e está dito na tela): o papel. Margens, entrelinha e
 * a geometria da folha continuam as da ABNT.
 */
import { normaNumerada, } from './tipos.js';
import { PARTICULAS_ESTRANGEIRAS, citacaoAutorData, dataNaReferencia, nomeNaReferencia, referenciaFormatada, referenciasEmOrdem, sobrenomeDoAutor, } from './abnt.js';
/** A norma do documento — a ABNT quando ele não escolheu nada (documento antigo). */
export function normaDoDocumento(documento) {
    return documento?.norma ?? 'ABNT';
}
/* ------------------------------------------------------------------ */
/* APA (7ª edição)                                                     */
/* ------------------------------------------------------------------ */
/**
 * `Silva` a partir de `SILVA` — a APA não escreve o sobrenome em caixa alta.
 *
 * A **partícula estrangeira fica em minúscula** ("van Gogh", "de la Cruz"), que é como a
 * APA escreve: ela anda colada ao sobrenome, e não é o começo dele.
 */
function caixaDeNome(sobrenome) {
    return sobrenome
        .toLowerCase()
        .split(/([ -])/)
        .map((pedaco) => {
        if (!/^[a-zà-ú]/.test(pedaco))
            return pedaco;
        if (PARTICULAS_ESTRANGEIRAS.has(pedaco))
            return pedaco;
        return pedaco.charAt(0).toUpperCase() + pedaco.slice(1);
    })
        .join('');
}
/** As iniciais do prenome: "João" vira "J.", "Ana Maria" vira "A. M.". */
function iniciais(prenome) {
    return prenome
        .split(/\s+/)
        .filter(Boolean)
        .map((palavra) => {
        /* Quem já digitou a inicial não ganha ponto duplo. */
        const letra = palavra.replace(/\./g, '').charAt(0);
        return letra ? `${letra.toUpperCase()}.` : '';
    })
        .filter(Boolean)
        .join(' ');
}
/**
 * O autor no formato da APA: `Silva, J.` — sobrenome em caixa mista e as iniciais do
 * prenome.
 *
 * Sai do mesmo lugar que a referência da ABNT (`nomeNaReferencia`), então as duas
 * normas nunca discordam sobre **qual** é o sobrenome — que é a única parte que não se
 * pode errar. Autor institucional sai como está ("IBGE"), sem inversão.
 */
export function autorNaApa(nome) {
    const naReferencia = nomeNaReferencia(nome);
    if (!naReferencia)
        return '';
    /*
     * Nome que não se pode inverter (uma palavra só) ou autor institucional: sai como está.
     * O motor não adivinha se "ARISTÓTELES" é instituição ou monônimo — mas **a caixa que a
     * pessoa digitou é respeitada**: quem escreveu "Aristóteles" (com minúsculas) recebe
     * "Aristóteles", e quem escreveu "IBGE" recebe "IBGE".
     */
    if (!naReferencia.includes(',')) {
        return /[a-zà-ú]/.test(nome.trim()) ? caixaDeNome(naReferencia) : naReferencia;
    }
    const [sobrenome, prenome] = naReferencia.split(',', 2);
    const letras = iniciais((prenome ?? '').trim());
    const familia = caixaDeNome((sobrenome ?? '').trim());
    return letras ? `${familia}, ${letras}` : familia;
}
/**
 * Os autores da APA: `Silva, J., & Souza, A. M.` — vírgulas e um `&` antes do último.
 *
 * A APA lista os autores até vinte; acima disso entra a elipse (primeiros dezenove,
 * reticências e o último). Aqui a lista sai inteira até vinte, e com `et al.` acima —
 * que é o que a norma admite quando a obra tem muitos autores e a linha não caberia.
 */
export function autoresNaApa(autores) {
    const nomes = autores.map(autorNaApa).filter(Boolean);
    if (nomes.length === 0)
        return '';
    if (nomes.length === 1)
        return nomes[0];
    if (nomes.length > 20)
        return `${nomes[0]}, et al.`;
    return `${nomes.slice(0, -1).join(', ')}, & ${nomes[nomes.length - 1]}`;
}
/** As páginas com travessão de intervalo: `15-30` vira `15–30` (é o que a APA usa). */
function paginasNaApa(paginas) {
    return paginas.replace(/\s*--?\s*/g, '–');
}
/** O título, em itálico (a marca `*` do motor, a mesma que o papel desenha). */
function emItalico(texto) {
    const limpo = texto.trim();
    return limpo ? `*${limpo}*` : '';
}
/**
 * A referência no padrão da APA (7ª edição).
 *
 * O que muda de verdade em relação à ABNT: o **ano vem logo depois dos autores**, entre
 * parênteses (é ele que ancora a citação autor-data); o **título fica em itálico** (na
 * ABNT é negrito, e só nos livros); e a **cidade não entra** em livro — a APA 7 tirou o
 * local da publicação da referência.
 *
 * O que o gerador **não tem** e por isso não inventa: o editor da obra (a APA escreve
 * "In A. Editor (Ed.), *Título*"), o DOI separado do endereço, e o tipo do trabalho
 * acadêmico (tese, dissertação ou TCC) — sai como *Trabalho acadêmico*, que é o que o
 * cadastro sabe dizer.
 */
/**
 * A data da APA: `2026, 12 de outubro` — o dia por extenso, que é como a norma escreve a
 * data dentro dos parênteses. Sem o dia, sai só o ano.
 */
function dataNaApa(referencia) {
    const ano = referencia.ano?.trim() ?? '';
    const data = referencia.dataPublicacao?.trim();
    if (!data)
        return ano;
    const [a, m, d] = data.split('-');
    const meses = [
        'janeiro',
        'fevereiro',
        'março',
        'abril',
        'maio',
        'junho',
        'julho',
        'agosto',
        'setembro',
        'outubro',
        'novembro',
        'dezembro',
    ];
    const mes = meses[Number(m) - 1];
    if (!mes)
        return a || ano;
    return d ? `${a}, ${Number(d)} de ${mes}` : `${a}, ${mes}`;
}
/** O que a APA escreve entre colchetes depois do título, por suporte. */
const SUPORTE_NA_APA = {
    VIDEO: '[Vídeo]',
    AUDIO: '[Áudio]',
    FILME: '[Filme]',
    IMAGEM: '[Imagem]',
};
export function referenciaNaApa(referencia) {
    if (referencia.tipo === 'LIVRE')
        return (referencia.texto ?? '').trim();
    const autores = autoresNaApa(referencia.autores);
    const titulo = [referencia.titulo.trim(), referencia.subtitulo?.trim()].filter(Boolean).join(': ');
    const ano = referencia.ano?.trim();
    /* O dia entra quando existe — e é o jornal e o vídeo que o trazem. */
    const data = ano || referencia.dataPublicacao?.trim() ? `(${dataNaApa(referencia)}).` : '';
    const partes = [];
    const inicio = [autores ? `${autores}` : '', data].filter(Boolean).join(' ');
    partes.push(inicio ? `${inicio} ` : '');
    switch (referencia.tipo) {
        case 'ARTIGO': {
            partes.push(titulo ? `${titulo}. ` : '');
            const periodico = referencia.local?.trim();
            if (periodico) {
                partes.push(`${emItalico(periodico)}`);
                if (referencia.volume?.trim())
                    partes.push(`, *${referencia.volume.trim()}*`);
                if (referencia.numero?.trim())
                    partes.push(`(${referencia.numero.trim()})`);
                if (referencia.paginas?.trim())
                    partes.push(`, ${paginasNaApa(referencia.paginas.trim())}`);
                partes.push('. ');
            }
            if (referencia.url?.trim())
                partes.push(`${referencia.url.trim()}`);
            break;
        }
        case 'SITE': {
            partes.push(titulo ? `${titulo}. ` : '');
            /* A APA 7 não pede a data de acesso: ela só entra quando o conteúdo muda. */
            if (referencia.local?.trim())
                partes.push(`${referencia.local.trim()}. `);
            if (referencia.url?.trim())
                partes.push(`${referencia.url.trim()}`);
            break;
        }
        case 'CAPITULO': {
            partes.push(titulo ? `${titulo}. ` : '');
            const obra = referencia.local?.trim();
            partes.push('In ');
            if (obra)
                partes.push(`${emItalico(obra)}`);
            if (referencia.paginas?.trim())
                partes.push(` (pp. ${paginasNaApa(referencia.paginas.trim())})`);
            partes.push('. ');
            if (referencia.editora?.trim())
                partes.push(`${referencia.editora.trim()}.`);
            break;
        }
        case 'TRABALHO': {
            partes.push(titulo ? `${emItalico(titulo)} ` : '');
            partes.push('(Trabalho acadêmico). ');
            if (referencia.instituicao?.trim())
                partes.push(`${referencia.instituicao.trim()}.`);
            else if (referencia.local?.trim())
                partes.push(`${referencia.local.trim()}.`);
            break;
        }
        case 'JORNAL': {
            partes.push(titulo ? `${titulo}. ` : '');
            const jornal = nomeDoPeriodico(referencia.local);
            if (jornal)
                partes.push(`${emItalico(jornal)}. `);
            if (referencia.secao?.trim())
                partes.push(`${referencia.secao.trim()}. `);
            if (referencia.paginas?.trim())
                partes.push(`p. ${referencia.paginas.trim()}. `);
            if (referencia.url?.trim())
                partes.push(`${referencia.url.trim()}`);
            break;
        }
        case 'REVISTA': {
            partes.push(titulo ? `${titulo}. ` : '');
            const revista = nomeDoPeriodico(referencia.local);
            if (revista) {
                partes.push(`${emItalico(revista)}`);
                /* O tomo da revista é o volume; sem ele, a edição faz as vezes. */
                const tomo = referencia.volume?.trim() || referencia.edicao?.trim();
                if (tomo)
                    partes.push(`, *${tomo}*`);
                if (referencia.numero?.trim())
                    partes.push(`(${referencia.numero.trim()})`);
                if (referencia.paginas?.trim())
                    partes.push(`, ${paginasNaApa(referencia.paginas.trim())}`);
                partes.push('. ');
            }
            if (referencia.url?.trim())
                partes.push(`${referencia.url.trim()}`);
            break;
        }
        case 'EVENTO': {
            partes.push(titulo ? `${titulo}. ` : '');
            partes.push('In ');
            const evento = referencia.local?.trim();
            if (evento)
                partes.push(`${emItalico(evento)}`);
            if (referencia.paginas?.trim())
                partes.push(` (pp. ${paginasNaApa(referencia.paginas.trim())})`);
            partes.push('. ');
            if (referencia.editora?.trim())
                partes.push(`${referencia.editora.trim()}.`);
            break;
        }
        case 'LEGISLACAO': {
            /* A APA escreve a norma pelo título (o da lei, com número e data) e o veículo. */
            partes.push(referencia.titulo.trim() ? `${referencia.titulo.trim()}. ` : '');
            if (referencia.subtitulo?.trim())
                partes.push(`${referencia.subtitulo.trim()}. `);
            const ondeSaiu = referencia.veiculo?.trim() || referencia.editora?.trim();
            if (ondeSaiu)
                partes.push(`${ondeSaiu}.`);
            break;
        }
        case 'VIDEO':
        case 'AUDIO':
        case 'FILME':
        case 'IMAGEM': {
            partes.push(titulo ? `${emItalico(titulo)} ` : '');
            const suporte = SUPORTE_NA_APA[referencia.tipo] ?? '';
            if (suporte)
                partes.push(`${suporte}. `);
            const casa = referencia.editora?.trim() || referencia.veiculo?.trim();
            if (casa)
                partes.push(`${casa}. `);
            if (referencia.url?.trim())
                partes.push(`${referencia.url.trim()}`);
            break;
        }
        case 'LIVRO':
        default: {
            partes.push(titulo ? `${emItalico(titulo)} ` : '');
            if (referencia.edicao?.trim())
                partes.push(`(${referencia.edicao.trim()}. ed.). `);
            /* Sem cidade: a APA 7 só quer a editora. */
            if (referencia.editora?.trim())
                partes.push(`${referencia.editora.trim()}.`);
            break;
        }
    }
    const linha = partes
        .join('')
        .replace(/\s+/g, ' ')
        .replace(/\s+\./g, '.')
        .replace(/\.\./g, '.')
        .trim();
    /* Fecha com ponto, como a norma escreve a linha — endereço no fim fica sem ponto. */
    if (!linha || /https?:\/\/\S+$/.test(linha))
        return linha;
    return linha.endsWith('.') ? linha : `${linha}.`;
}
/**
 * A citação no texto da APA: `(Silva, 2024, p. 15)`.
 *
 * Duas diferenças da ABNT: o sobrenome em **caixa mista** (a ABNT escreve em caixa
 * alta) e o **`&`** entre dois autores (a ABNT separa com ponto e vírgula). Com três ou
 * mais, as duas escrevem `et al.`.
 */
export function citacaoNaApa(referencia, pagina) {
    const sobrenomes = referencia.autores
        .map((nome) => caixaDeNome(sobrenomeDoAutor(nome)))
        .filter(Boolean);
    const ano = referencia.ano?.trim();
    if (sobrenomes.length === 0 || !ano)
        return '';
    const autores = sobrenomes.length === 1
        ? sobrenomes[0]
        : sobrenomes.length === 2
            ? `${sobrenomes[0]} & ${sobrenomes[1]}`
            : `${sobrenomes[0]} et al.`;
    const pag = pagina?.trim();
    return `(${autores}, ${ano}${pag ? `, p. ${pag}` : ''})`;
}
/* ------------------------------------------------------------------ */
/* IEEE e Vancouver: as normas numeradas                               */
/* ------------------------------------------------------------------ */
/**
 * O autor com as iniciais **na frente** do sobrenome: `J. Silva` (IEEE).
 *
 * A ordem é o contrário da APA, e é a diferença que mais aparece na lista. Autor
 * institucional sai inteiro ("IBGE"), como nas outras normas.
 */
export function autorNoIeee(nome, comIniciais) {
    const naReferencia = nomeNaReferencia(nome);
    if (!naReferencia)
        return '';
    if (!naReferencia.includes(',')) {
        return comIniciais && /[a-zà-ú]/.test(nome.trim()) ? caixaDeNome(naReferencia) : naReferencia;
    }
    const [sobrenome, prenome] = naReferencia.split(',', 2);
    const familia = caixaDeNome((sobrenome ?? '').trim());
    const letras = iniciais((prenome ?? '').trim());
    return letras ? `${letras} ${familia}` : familia;
}
/**
 * O autor no formato do Vancouver: `Silva J` — sobrenome **na frente** e as iniciais
 * coladas, sem ponto e sem espaço entre elas ("Souza AM").
 */
export function autorNoVancouver(nome) {
    const naReferencia = nomeNaReferencia(nome);
    if (!naReferencia)
        return '';
    if (!naReferencia.includes(',')) {
        return /[a-zà-ú]/.test(nome.trim()) ? caixaDeNome(naReferencia) : naReferencia;
    }
    const [sobrenome, prenome] = naReferencia.split(',', 2);
    const familia = caixaDeNome((sobrenome ?? '').trim());
    const letras = iniciais((prenome ?? '').trim()).replace(/[.\s]/g, '');
    return letras ? `${familia} ${letras}` : familia;
}
/**
 * Os autores das normas numeradas.
 *
 * As duas listam até **seis** autores e depois resumem com `et al.` — o IEEE separa com
 * vírgula e um `and` antes do último; o Vancouver separa só com vírgulas.
 */
export function autoresNumerados(autores, norma) {
    const nomes = norma === 'VANCOUVER' ? autores.map(autorNoVancouver) : autores.map((nome) => autorNoIeee(nome, true));
    const limpos = nomes.filter(Boolean);
    if (limpos.length === 0)
        return '';
    if (limpos.length > 6)
        return `${limpos[0]}, et al.`;
    if (limpos.length === 1)
        return limpos[0];
    if (norma === 'VANCOUVER')
        return limpos.join(', ');
    return `${limpos.slice(0, -1).join(', ')}${limpos.length > 2 ? ',' : ''} and ${limpos[limpos.length - 1]}`;
}
/** As páginas com travessão, como as duas escrevem intervalos. */
const paginasComTravessao = paginasNaApa;
/** O título em itálico (livro e nome de revista nas normas numeradas). */
const destaque = emItalico;
/**
 * O nome do periódico **sem a cidade**: `Folha de S.Paulo, São Paulo` → `Folha de S.Paulo`.
 *
 * A NBR destaca o nome e escreve a cidade depois; a APA e o Vancouver não escrevem a cidade
 * do jornal, então aqui ela fica de fora.
 */
function nomeDoPeriodico(local) {
    const limpo = (local ?? '').trim();
    if (!limpo)
        return '';
    return limpo.split(',')[0].trim();
}
/**
 * O ano e o mês abreviado, como o Vancouver escreve a revista: `2026 out.`.
 *
 * Sem a data inteira sai só o ano — que é o que quem não tem o dia sabe dizer.
 */
function mesAnoNaMidia(referencia) {
    const ano = referencia.ano?.trim() ?? '';
    const data = referencia.dataPublicacao?.trim();
    if (!data)
        return ano;
    const abreviadas = ['jan.', 'fev.', 'mar.', 'abr.', 'maio', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];
    const [anoDaData, mesDaData] = data.split('-');
    const mes = abreviadas[Number(mesDaData) - 1];
    return mes ? `${anoDaData || ano} ${mes}` : anoDaData || ano;
}
/** A data da edição como as numeradas escrevem: `12 out. 2026`; sem o dia, o ano seco. */
function quandoSaiuNaMidia(referencia) {
    const data = referencia.dataPublicacao?.trim();
    if (data)
        return dataNaReferencia(data);
    return referencia.ano?.trim() ?? '';
}
/** O que a norma numerada escreve entre colchetes, por suporte — com o tempo junto. */
function suporteNoIeee(referencia) {
    const nome = {
        VIDEO: 'Vídeo on-line',
        AUDIO: 'Áudio on-line',
        FILME: 'Filme',
        IMAGEM: 'Imagem on-line',
    }[referencia.tipo];
    if (!nome)
        return '';
    const duracao = referencia.duracao?.trim();
    return duracao ? `[${nome}, ${duracao}]` : `[${nome}]`;
}
/** O mesmo suporte no Vancouver, que escreve em minúsculas e sem travessão. */
function suporteNoVancouver(referencia) {
    const nome = {
        VIDEO: 'vídeo',
        AUDIO: 'áudio',
        FILME: 'filme',
        IMAGEM: 'imagem',
    }[referencia.tipo];
    if (!nome)
        return '';
    const duracao = referencia.duracao?.trim();
    return duracao ? `[${nome}, ${duracao}]` : `[${nome}]`;
}
/**
 * A referência no padrão do **IEEE**.
 *
 * O que muda em relação à ABNT: o autor traz as **iniciais na frente** (`J. Silva`), o
 * título do artigo sai **entre aspas**, a revista vai em itálico com `vol.`, `no.` e
 * `pp.`, e o livro traz a edição em inglês curto (`3rd ed.` é o que a norma escreve — aqui
 * sai `3. ed.`, que é como se lê em português, e está dito na nota).
 *
 * O que o gerador **não tem** e não inventa: o grau do trabalho acadêmico (a norma escreve
 * "M.S. thesis" ou "Ph.D. dissertation") e o departamento — sai `(Trabalho acadêmico)` e a
 * instituição, que é o que o cadastro sabe.
 */
export function referenciaNoIeee(referencia, numero) {
    if (referencia.tipo === 'LIVRE')
        return (referencia.texto ?? '').trim();
    const autores = autoresNumerados(referencia.autores, 'IEEE');
    const titulo = [referencia.titulo.trim(), referencia.subtitulo?.trim()].filter(Boolean).join(': ');
    const ano = referencia.ano?.trim();
    const prefixo = numero ? `[${numero}] ` : '';
    const partes = [];
    partes.push(autores ? `${autores}, ` : '');
    switch (referencia.tipo) {
        case 'ARTIGO': {
            if (titulo)
                partes.push(`"${titulo}," `);
            if (referencia.local?.trim())
                partes.push(`${destaque(referencia.local.trim())}`);
            if (referencia.volume?.trim())
                partes.push(`, vol. ${referencia.volume.trim()}`);
            if (referencia.numero?.trim())
                partes.push(`, no. ${referencia.numero.trim()}`);
            if (referencia.paginas?.trim())
                partes.push(`, pp. ${paginasComTravessao(referencia.paginas.trim())}`);
            partes.push(ano ? `, ${ano}.` : '.');
            if (referencia.url?.trim())
                partes.push(` ${referencia.url.trim()}`);
            break;
        }
        case 'SITE': {
            if (titulo)
                partes.push(`"${titulo}," `);
            if (referencia.local?.trim())
                partes.push(`${referencia.local.trim()}. `);
            partes.push('[Online]. ');
            if (referencia.url?.trim())
                partes.push(`Disponível em: ${referencia.url.trim()}. `);
            partes.push(referencia.acessoEm?.trim() ? `Acesso em: ${dataNaReferencia(referencia.acessoEm)}.` : '');
            break;
        }
        case 'CAPITULO': {
            if (titulo)
                partes.push(`"${titulo}," `);
            partes.push('in ');
            if (referencia.local?.trim())
                partes.push(`${destaque(referencia.local.trim())}`);
            if (referencia.editora?.trim())
                partes.push(`, ${referencia.editora.trim()}`);
            partes.push(ano ? `, ${ano}` : '');
            if (referencia.paginas?.trim())
                partes.push(`, pp. ${paginasComTravessao(referencia.paginas.trim())}`);
            partes.push('.');
            break;
        }
        case 'TRABALHO': {
            if (titulo)
                partes.push(`"${titulo}," `);
            partes.push('(Trabalho acadêmico), ');
            if (referencia.instituicao?.trim())
                partes.push(`${referencia.instituicao.trim()}, `);
            if (referencia.local?.trim())
                partes.push(`${referencia.local.trim()}, `);
            partes.push(ano ? `${ano}.` : '.');
            break;
        }
        case 'JORNAL': {
            if (titulo)
                partes.push(`"${titulo}," `);
            const jornal = referencia.local?.trim();
            if (jornal)
                partes.push(`${destaque(jornal)}`);
            if (referencia.secao?.trim())
                partes.push(`, ${referencia.secao.trim()}`);
            const quando = quandoSaiuNaMidia(referencia);
            partes.push(quando ? `, ${quando}` : '');
            if (referencia.paginas?.trim()) {
                partes.push(`, pp. ${paginasComTravessao(referencia.paginas.trim())}`);
            }
            partes.push('.');
            if (referencia.url?.trim())
                partes.push(` ${referencia.url.trim()}`);
            break;
        }
        case 'REVISTA': {
            if (titulo)
                partes.push(`"${titulo}," `);
            const revista = referencia.local?.trim();
            if (revista)
                partes.push(`${destaque(revista)}`);
            if (referencia.edicao?.trim())
                partes.push(`, ed. ${referencia.edicao.trim()}`);
            if (referencia.volume?.trim())
                partes.push(`, vol. ${referencia.volume.trim()}`);
            if (referencia.numero?.trim())
                partes.push(`, no. ${referencia.numero.trim()}`);
            if (referencia.paginas?.trim()) {
                partes.push(`, pp. ${paginasComTravessao(referencia.paginas.trim())}`);
            }
            const quando = quandoSaiuNaMidia(referencia);
            partes.push(quando ? `, ${quando}` : '');
            partes.push('.');
            if (referencia.url?.trim())
                partes.push(` ${referencia.url.trim()}`);
            break;
        }
        case 'EVENTO': {
            if (titulo)
                partes.push(`"${titulo}," `);
            partes.push('in ');
            if (referencia.local?.trim())
                partes.push(`${destaque(referencia.local.trim())}`);
            if (referencia.numero?.trim())
                partes.push(`, ${referencia.numero.trim()}. ed.`);
            partes.push(ano ? `, ${ano}` : '');
            if (referencia.paginas?.trim()) {
                partes.push(`, pp. ${paginasComTravessao(referencia.paginas.trim())}`);
            }
            partes.push('.');
            if (referencia.url?.trim())
                partes.push(` ${referencia.url.trim()}`);
            break;
        }
        case 'LEGISLACAO': {
            if (titulo)
                partes.push(`"${titulo}," `);
            const ondeSaiu = referencia.veiculo?.trim() || referencia.editora?.trim();
            if (ondeSaiu)
                partes.push(`${ondeSaiu}`);
            partes.push(ano ? `${ondeSaiu ? ', ' : ''}${ano}.` : '.');
            if (referencia.url?.trim())
                partes.push(` ${referencia.url.trim()}`);
            break;
        }
        case 'VIDEO':
        case 'AUDIO':
        case 'FILME':
        case 'IMAGEM': {
            if (titulo)
                partes.push(`"${titulo}," `);
            const ondeSaiu = referencia.editora?.trim() || referencia.veiculo?.trim();
            const quando = quandoSaiuNaMidia(referencia);
            const cabeca = [ondeSaiu, quando].filter(Boolean).join(', ');
            if (cabeca)
                partes.push(cabeca);
            const suporte = suporteNoIeee(referencia);
            if (suporte)
                partes.push(`${cabeca ? '. ' : ''}${suporte}`);
            partes.push('. ');
            if (referencia.url?.trim())
                partes.push(`Disponível em: ${referencia.url.trim()}. `);
            partes.push(referencia.acessoEm?.trim() ? `Acesso em: ${dataNaReferencia(referencia.acessoEm)}.` : '');
            break;
        }
        case 'LIVRO':
        default: {
            if (titulo)
                partes.push(`${destaque(titulo)}`);
            if (referencia.edicao?.trim())
                partes.push(`, ${referencia.edicao.trim()}. ed.`);
            if (referencia.local?.trim())
                partes.push(` ${referencia.local.trim()}:`);
            if (referencia.editora?.trim())
                partes.push(` ${referencia.editora.trim()}`);
            partes.push(ano ? `, ${ano}.` : '.');
            break;
        }
    }
    return `${prefixo}${partes
        .join('')
        .replace(/\s+/g, ' ')
        .replace(/\s+\./g, '.')
        .replace(/\.\./g, '.')
        .trim()}`;
}
/**
 * A referência no padrão do **Vancouver** (a forma brasileira, que é a do ICMJE com os
 * rótulos em português).
 *
 * O autor sai com o sobrenome e as iniciais **coladas** (`Silva J`), o título do artigo não
 * tem aspas nem destaque, e o periódico fecha em `ano;volume(número):páginas`. O livro traz
 * a cidade e a editora separadas por `:` e o ano depois de `;`. O trabalho acadêmico leva o
 * grau entre colchetes — sai `[trabalho acadêmico]`, que é o que o cadastro sabe dizer.
 */
export function referenciaNoVancouver(referencia, numero) {
    if (referencia.tipo === 'LIVRE')
        return (referencia.texto ?? '').trim();
    const autores = autoresNumerados(referencia.autores, 'VANCOUVER');
    const titulo = [referencia.titulo.trim(), referencia.subtitulo?.trim()].filter(Boolean).join(': ');
    const ano = referencia.ano?.trim();
    const prefixo = numero ? `${numero}. ` : '';
    const partes = [];
    switch (referencia.tipo) {
        case 'ARTIGO': {
            partes.push(autores ? `${autores}. ` : '');
            if (titulo)
                partes.push(`${titulo}. `);
            if (referencia.local?.trim())
                partes.push(`${referencia.local.trim()}. `);
            partes.push(ano ? `${ano}` : '');
            if (referencia.volume?.trim())
                partes.push(`;${referencia.volume.trim()}`);
            if (referencia.numero?.trim())
                partes.push(`(${referencia.numero.trim()})`);
            if (referencia.paginas?.trim())
                partes.push(`:${referencia.paginas.trim()}`);
            partes.push('.');
            if (referencia.url?.trim())
                partes.push(` Disponível em: ${referencia.url.trim()}.`);
            break;
        }
        case 'SITE': {
            partes.push(autores ? `${autores}. ` : '');
            if (titulo)
                partes.push(`${titulo} [Internet]. `);
            if (referencia.local?.trim())
                partes.push(`${referencia.local.trim()}`);
            if (referencia.editora?.trim())
                partes.push(`: ${referencia.editora.trim()}`);
            partes.push(ano ? `; ${ano}` : '');
            partes.push(referencia.acessoEm?.trim() ? ` [citado ${dataNaReferencia(referencia.acessoEm)}]` : '');
            partes.push('. ');
            if (referencia.url?.trim())
                partes.push(`Disponível em: ${referencia.url.trim()}.`);
            break;
        }
        case 'CAPITULO': {
            partes.push(autores ? `${autores}. ` : '');
            if (titulo)
                partes.push(`${titulo}. `);
            partes.push('In: ');
            if (referencia.local?.trim())
                partes.push(`${destaque(referencia.local.trim())}. `);
            if (referencia.editora?.trim())
                partes.push(`${referencia.editora.trim()}`);
            if (referencia.local?.trim() === '' && referencia.editora?.trim())
                partes.push('');
            partes.push(ano ? `; ${ano}` : '');
            partes.push('. ');
            if (referencia.paginas?.trim())
                partes.push(`p. ${referencia.paginas.trim()}.`);
            break;
        }
        case 'TRABALHO': {
            partes.push(autores ? `${autores}. ` : '');
            if (titulo)
                partes.push(`${destaque(titulo)} [trabalho acadêmico]. `);
            if (referencia.local?.trim())
                partes.push(`${referencia.local.trim()}`);
            if (referencia.instituicao?.trim())
                partes.push(`: ${referencia.instituicao.trim()}`);
            partes.push(ano ? `; ${ano}` : '');
            partes.push('.');
            break;
        }
        case 'JORNAL': {
            partes.push(autores ? `${autores}. ` : '');
            if (titulo)
                partes.push(`${titulo}. `);
            const jornal = nomeDoPeriodico(referencia.local);
            if (jornal)
                partes.push(`${destaque(jornal)}. `);
            if (referencia.secao?.trim())
                partes.push(`${referencia.secao.trim()}. `);
            const quando = quandoSaiuNaMidia(referencia);
            partes.push(quando ? `${quando}. ` : '');
            if (referencia.paginas?.trim())
                partes.push(`p. ${referencia.paginas.trim()}. `);
            if (referencia.url?.trim())
                partes.push(`Disponível em: ${referencia.url.trim()}. `);
            partes.push(referencia.acessoEm?.trim() ? `[citado ${dataNaReferencia(referencia.acessoEm)}].` : '');
            break;
        }
        case 'REVISTA': {
            partes.push(autores ? `${autores}. ` : '');
            if (titulo)
                partes.push(`${titulo}. `);
            const revista = nomeDoPeriodico(referencia.local);
            if (revista)
                partes.push(`${destaque(revista)}. `);
            const quando = mesAnoNaMidia(referencia);
            partes.push(quando ? `${quando}` : '');
            const tomo = referencia.volume?.trim() || referencia.edicao?.trim();
            if (tomo)
                partes.push(`;${tomo}`);
            if (referencia.numero?.trim())
                partes.push(`(${referencia.numero.trim()})`);
            if (referencia.paginas?.trim())
                partes.push(`:${referencia.paginas.trim()}`);
            partes.push('.');
            if (referencia.url?.trim())
                partes.push(` Disponível em: ${referencia.url.trim()}.`);
            break;
        }
        case 'EVENTO': {
            partes.push(autores ? `${autores}. ` : '');
            if (titulo)
                partes.push(`${titulo}. `);
            partes.push('In: ');
            if (referencia.local?.trim())
                partes.push(`${destaque(referencia.local.trim())}. `);
            partes.push(`${destaque('Anais do evento')}. `);
            if (referencia.editora?.trim())
                partes.push(`${referencia.editora.trim()}`);
            partes.push(ano ? `; ${ano}` : '');
            partes.push('. ');
            if (referencia.paginas?.trim())
                partes.push(`p. ${referencia.paginas.trim()}.`);
            break;
        }
        case 'LEGISLACAO': {
            partes.push(autores ? `${autores}. ` : '');
            if (referencia.titulo.trim())
                partes.push(`${referencia.titulo.trim()}. `);
            if (referencia.subtitulo?.trim())
                partes.push(`${referencia.subtitulo.trim()}. `);
            const ondeSaiu = referencia.veiculo?.trim();
            if (ondeSaiu)
                partes.push(`${ondeSaiu}. `);
            else if (referencia.local?.trim() || referencia.editora?.trim()) {
                if (referencia.local?.trim())
                    partes.push(referencia.local.trim());
                if (referencia.editora?.trim())
                    partes.push(`: ${referencia.editora.trim()}`);
                partes.push('. ');
            }
            partes.push(ano ? `${ano}.` : '');
            break;
        }
        case 'VIDEO':
        case 'AUDIO':
        case 'FILME':
        case 'IMAGEM': {
            partes.push(autores ? `${autores}. ` : '');
            const suporte = suporteNoVancouver(referencia);
            if (titulo)
                partes.push(`${titulo} ${suporte}. `);
            const ondeSaiu = referencia.editora?.trim() || referencia.veiculo?.trim();
            if (ondeSaiu)
                partes.push(`${ondeSaiu}. `);
            partes.push(ano ? `${ano}. ` : '');
            if (referencia.url?.trim())
                partes.push(`Disponível em: ${referencia.url.trim()}. `);
            partes.push(referencia.acessoEm?.trim() ? `[citado ${dataNaReferencia(referencia.acessoEm)}].` : '');
            break;
        }
        case 'LIVRO':
        default: {
            partes.push(autores ? `${autores}. ` : '');
            if (titulo)
                partes.push(`${destaque(titulo)}. `);
            if (referencia.edicao?.trim())
                partes.push(`${referencia.edicao.trim()}. ed. `);
            if (referencia.local?.trim())
                partes.push(`${referencia.local.trim()}`);
            if (referencia.editora?.trim())
                partes.push(`: ${referencia.editora.trim()}`);
            partes.push(ano ? `; ${ano}` : '');
            partes.push('.');
            break;
        }
    }
    return `${prefixo}${partes
        .join('')
        .replace(/\s+/g, ' ')
        .replace(/\s+\./g, '.')
        .replace(/\.\./g, '.')
        .trim()}`;
}
/** A citação no texto das normas numeradas: o número, com a página quando houver. */
export function citacaoNumerada(numero, norma, pagina) {
    if (!numero)
        return '';
    const pag = pagina?.trim();
    const dentro = pag ? `${numero}, p. ${pag}` : `${numero}`;
    return norma === 'VANCOUVER' ? `(${dentro})` : `[${dentro}]`;
}
/**
 * O próximo número livre da lista numerada.
 *
 * É a conta que `listaDaNorma` usa para numerar provisoriamente o que ainda não foi citado —
 * e o gesto de **citar** grava exatamente esse número, para o que a pessoa vê na lista ser o
 * que a citação escreve. Com ele, quem é citado primeiro fica com o 1 e a lista sai na ordem
 * em que as obras aparecem no texto, que é a regra do IEEE e do Vancouver.
 */
export function proximoNumero(referencias) {
    const usados = referencias
        .map((referencia) => referencia.numeroDaLista)
        .filter((numero) => typeof numero === 'number' && numero > 0);
    return usados.length === 0 ? 1 : Math.max(...usados) + 1;
}
/**
 * A lista de referências na ordem da norma, **com o número de cada uma**.
 *
 * Nas normas autor-data (ABNT, APA) a ordem é a alfabética de sempre e não há número. Nas
 * numeradas, a ordem é a **do número** — e a obra que ainda não foi citada entra no fim,
 * em ordem alfabética, com um número provisório, para a lista nunca sair sem numeração
 * (o motor avisa quais são provisórios).
 */
export function listaDaNorma(referencias, norma) {
    if (!normaNumerada(norma)) {
        return referenciasEmOrdem(referencias).map((referencia) => ({
            referencia,
            numero: null,
            provisorio: false,
        }));
    }
    const temNumero = (referencia) => typeof referencia.numeroDaLista === 'number' && referencia.numeroDaLista > 0;
    const itens = referencias
        .filter(temNumero)
        .slice()
        .sort((a, b) => a.numeroDaLista - b.numeroDaLista)
        .map((referencia) => ({ referencia, numero: referencia.numeroDaLista, provisorio: false }));
    /*
     * A obra que ainda **não** foi citada entra no fim, e o número provisório segue a ordem
     * em que ela foi cadastrada — não a alfabética. A diferença importa: é essa mesma ordem
     * que o gesto de **citar** usa para dar o número, então o número que a pessoa vê na lista
     * é o número que a citação grava (e nada muda de lugar na frente dela depois de citar).
     */
    let proximo = proximoNumero(referencias);
    for (const referencia of referencias.filter((item) => !temNumero(item))) {
        itens.push({ referencia, numero: proximo, provisorio: true });
        proximo += 1;
    }
    return itens;
}
/* ------------------------------------------------------------------ */
/* A porta única: a norma escolhe a forma                              */
/* ------------------------------------------------------------------ */
/** A linha da lista de referências, na norma do documento (com o número, se houver). */
export function linhaDaReferencia(referencia, norma = 'ABNT', numero) {
    if (norma === 'APA')
        return referenciaNaApa(referencia);
    if (norma === 'IEEE')
        return referenciaNoIeee(referencia, numero ?? referencia.numeroDaLista);
    if (norma === 'VANCOUVER') {
        return referenciaNoVancouver(referencia, numero ?? referencia.numeroDaLista);
    }
    return referenciaFormatada(referencia);
}
/**
 * A citação que entra no texto, na norma do documento.
 *
 * Nas autor-data (ABNT, APA) ela sai do autor e do ano; nas numeradas (IEEE, Vancouver), do
 * **número** — e sem número não há citação, porque `[ ]` vazio não diz nada a ninguém.
 */
export function citacaoDaReferencia(referencia, norma = 'ABNT', pagina) {
    if (norma === 'APA')
        return citacaoNaApa(referencia, pagina);
    if (normaNumerada(norma))
        return citacaoNumerada(referencia.numeroDaLista, norma, pagina);
    return citacaoAutorData(referencia, pagina);
}
export const RESUMO_DA_NORMA = {
    ABNT: {
        nome: 'ABNT (NBR 6023 e NBR 10520)',
        lista: 'SOBRENOME, Nome. **Título**. Cidade: Editora, ano.',
        citacao: '(SOBRENOME, ano, p. página)',
        diferenca: 'É a norma brasileira de trabalhos acadêmicos, e a que o gerador usa desde o começo.',
    },
    APA: {
        nome: 'APA (7ª edição)',
        lista: 'Sobrenome, I. (ano). *Título*. Editora.',
        citacao: '(Sobrenome, ano, p. página)',
        diferenca: 'O ano vem logo depois dos autores, o título fica em itálico, a cidade sai da referência e o sobrenome não vai em caixa alta.',
    },
    IEEE: {
        nome: 'IEEE',
        lista: '[1] J. Silva, "Título do artigo," *Revista*, vol. 29, no. 1, pp. 15–30, 2024.',
        citacao: '[1]',
        diferenca: 'A lista é **numerada na ordem em que as obras são citadas**, e a citação no texto é o número. O autor traz as iniciais na frente (J. Silva) e a revista vai em itálico.',
    },
    VANCOUVER: {
        nome: 'Vancouver',
        lista: '1. Silva J. Título do artigo. Revista. 2024;29(1):15-30.',
        citacao: '(1)',
        diferenca: 'A lista é **numerada na ordem em que as obras são citadas**, com o sobrenome e as iniciais coladas (Silva J) e o periódico fechando em ano;volume(número):páginas.',
    },
};
//# sourceMappingURL=normas.js.map